import { getAPIUrl } from "config/api";

import { api, publicApi } from ".";

/**
 * CLE-2718 — Finix PCI SAQ A compliance forms.
 *
 * Split across both API instances on purpose. The admin half rides the authenticated `api` and is
 * SuperAdmin-gated server-side; the attestation half rides `publicApi` because the person signing
 * is a hospital executive with no ClearHealth account.
 */

export interface IFinixComplianceForm {
  finixFormId: string;
  finixMerchantId: string | null;
  type: string;
  state: string;
  dueAt: string | null;
  isAccepted: boolean;
  signedAt: string | null;
  attestedName: string | null;
  attestedTitle: string | null;
  hasSignedFile: boolean;
  actionable: boolean;
  /** Null until a link has been issued, and again once it expires. */
  attestationUrl: string | null;
  attestationUrlExpiresAt: string | null;
  lastSyncedAt: string | null;
}

export interface IFinixComplianceOverview {
  /**
   * Keyed by Finix merchant id, not by provider.
   *
   * One merchant appears on several provider rows in the same account, and each of those rows
   * renders the same form, status and link. A per-provider shape would imply N separate
   * attestations where there is only ever one signature.
   */
  formsByMerchantId: Record<string, IFinixComplianceForm[]>;
  outstandingCount: number;
  overdueCount: number;
}

export interface IFinixComplianceLink {
  finixFormId: string;
  attestationUrl: string;
  expiresAt: string;
}

export interface IFinixComplianceReconcileResult {
  fetched: number;
  upserted: number;
  pages: number;
  truncated: boolean;
}

const finixComplianceQueries = api.injectEndpoints({
  endpoints: (builder) => ({
    getAccountComplianceOverview: builder.query<
      IFinixComplianceOverview,
      string
    >({
      query: (accountId) => ({
        url: `/finix/compliance/account/${accountId}`,
        method: "GET",
      }),
      providesTags: ["FinixCompliance"],
    }),
    listComplianceForms: builder.query<{ forms: IFinixComplianceForm[] }, void>(
      {
        query: () => ({ url: "/finix/compliance/forms", method: "GET" }),
        providesTags: ["FinixCompliance"],
      }
    ),
    issueComplianceLink: builder.mutation<IFinixComplianceLink, string>({
      query: (finixFormId) => ({
        url: `/finix/compliance/${finixFormId}/link`,
        method: "POST",
      }),
      invalidatesTags: ["FinixCompliance"],
    }),
    reconcileComplianceForms: builder.mutation<
      IFinixComplianceReconcileResult,
      void
    >({
      query: () => ({ url: "/finix/compliance/reconcile", method: "POST" }),
      invalidatesTags: ["FinixCompliance"],
    }),
  }),
});

// ===== Public (tokenised) =====

export interface IPublicComplianceForm {
  type: string;
  state: string;
  dueAt: string | null;
  isAccepted: boolean;
  signedAt: string | null;
  prefillName: string | null;
  prefillTitle: string | null;
  canAttest: boolean;
}

export interface IComplianceAttestationResult {
  state: string;
  isAccepted: boolean;
  signedAt: string | null;
  signedFileAvailable: boolean;
}

const publicComplianceQueries = publicApi.injectEndpoints({
  endpoints: (builder) => ({
    getPublicComplianceForm: builder.query<IPublicComplianceForm, string>({
      query: (token) => ({
        url: `/finix/compliance/public/${token}`,
        method: "GET",
      }),
    }),
    submitComplianceAttestation: builder.mutation<
      IComplianceAttestationResult,
      { token: string; name: string; title: string; captchaToken: string }
    >({
      query: ({ token, name, title, captchaToken }) => ({
        url: `/finix/compliance/public/${token}/attest`,
        method: "POST",
        // `accepted` is always true here — the button that fires this mutation is disabled until
        // the checkbox is ticked. It is still sent because the API requires it: the server must be
        // able to reject an attestation that was never accepted, independently of our UI.
        body: { name, title, accepted: true, captchaToken },
      }),
    }),
  }),
});

/**
 * Absolute URL of the PDF proxy, handed straight to react-pdf.
 *
 * Not an RTK Query endpoint: pdf.js does its own fetching, and the document is public + token-bound
 * so there is no header to attach. It must never point at Finix — the browser cannot hold our
 * credentials, and Finix serves the file with `X-Frame-Options: DENY`.
 */
export const complianceFormPdfUrl = (token: string): string =>
  `${getAPIUrl().replace(/\/+$/, "")}/finix/compliance/public/${token}/pdf`;

/**
 * Same document, but asking the API for `Content-Disposition: attachment` so the browser saves the
 * file instead of handing it to its own PDF viewer. Kept separate from the render URL so the two
 * never collide — react-pdf must keep receiving the inline form.
 */
export const complianceFormPdfDownloadUrl = (token: string): string =>
  `${complianceFormPdfUrl(token)}?download=1`;

export const {
  useGetAccountComplianceOverviewQuery,
  useListComplianceFormsQuery,
  useIssueComplianceLinkMutation,
  useReconcileComplianceFormsMutation,
} = finixComplianceQueries;

export const {
  useGetPublicComplianceFormQuery,
  useSubmitComplianceAttestationMutation,
} = publicComplianceQueries;
